import classNames from 'classnames';
import {SelectionRange, type TextSelection} from 'prosemirror-state';
import {type MarkType, type Node} from 'prosemirror-model';
import {
    callOrReturn,
    type EditorEvents,
    InputRule,
    type Mark,
    markInputRule,
    mergeAttributes,
} from '@tiptap/core';
import {Link} from '@tiptap/extension-link';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import type {LinkOptions as OriginalLinkOptions} from '@tiptap/extension-link';
import {find} from 'linkifyjs';
import {Decoration, DecorationSet} from 'prosemirror-view';
import {sendToStatist} from 'packages/statist';

import {clearFormatStoredMarks} from '../utils';
import appStyles from '../app-post-style.module.css';
import {hasProtocol} from '../../components/link-popovers/utils';

type LinkAttrs = {
    href: string;
    title?: string;
}

export interface LinkOptions extends OriginalLinkOptions {
    excludedMarks: Mark[];
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        linkCommands: {
            addLinkToSelection: (attrs: LinkAttrs) => ReturnType;
        };
    }
}

const protocols = new Set(['ftp', 'mailto', 'timeapp', 'http', 'https']);

export const dataLinkPreSelectionAttribute = 'data-pre-link-selection';
export const dataLinkPopoverTarget = 'data-link-popover-target';
export const LINK_POPOVER_EVENT = 'LINK_POPOVER_EVENT' as keyof EditorEvents;

export const linkPluginKey = new PluginKey('linkPlugin');

function linkInputRule(config: Parameters<typeof markInputRule>[0]) {
    const defaultMarkInputRule = markInputRule(config);

    return new InputRule({
        find: config.find,
        handler(props) {
            const attributes = callOrReturn(config.getAttributes, undefined, props.match.slice());

            if (!attributes) {
                return null;
            }

            const protocol = attributes.href?.split(':')[0];

            if (protocol && !protocols.has(protocol.toLowerCase())) {
                return null;
            }

            defaultMarkInputRule.handler(props);
            clearFormatStoredMarks(props.state);
            props.state.tr.setMeta('preventAutolink', true);
        },
    });
}

/**
 * @see https://github.com/ueberdosis/tiptap/discussions/1865
 */
export const TimeWebkitLinkExtension = Link.extend<LinkOptions>({
    inclusive: false,
    renderHTML({HTMLAttributes}) {
        return [
            'a',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                class: classNames(HTMLAttributes.class, appStyles.markdown__link),
                title: HTMLAttributes.title || HTMLAttributes.href,
            }),
            0,
        ];
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            title: {
                default: null,
            },
        };
    },
    addInputRules() {
        return [
            linkInputRule({
                find: /(?:^|\s)\[([^\]]*)?]\((\S+)(?: ["“](.+)["”])?\)\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        title: match.pop()?.trim(),
                        href: match.pop()?.trim(),
                    };
                },
            }),
            linkInputRule({
                find: /(?:^|\s)<([a-zA-Z]+:\S+)>\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        href: match[match.length - 1]?.trim(),
                    };
                },
            }),
        ];
    },
    addCommands() {
        return {
            addLinkToSelection: (attrs) => ({state, dispatch}) => {
                const markType = this.type;
                const {empty, $cursor, ranges: initialRanges} = state.selection as TextSelection;
                const ranges = filterRanges(initialRanges, this.options.excludedMarks);
                const enterAtoms = false;
                if ($cursor) {
                    return false;
                }
                if (empty || !markApplies(state.doc, ranges, markType, enterAtoms)) {
                    return false;
                }
                if (dispatch) {
                    const tr = state.tr;
                    for (const next of ranges) {
                        const {$from, $to} = next;
                        dispatch(tr.addMark($from.pos, $to.pos, markType.create(attrs)));
                    }
                }
                return true;
            },
        };
    },
    addProseMirrorPlugins() {
        return [
            ...this.parent?.() || [],
            new Plugin({
                key: linkPluginKey,
                state: {
                    init: () => ({shouldHighlight: false}),
                    apply(tr, prev) {
                        const meta = tr.getMeta(linkPluginKey);
                        return meta ? {...prev, ...meta} : prev;
                    },
                },
                props: {
                    handlePaste: (view, _event, slice) => {
                        let textContent = '';

                        slice.content.forEach((node) => {
                            textContent += node.textContent;
                        });

                        const link = find(textContent, {
                            defaultProtocol: this.options.defaultProtocol,
                        }).find(
                            (item) => item.isLink && item.value === textContent,
                        );

                        if (!textContent || !link) {
                            return false;
                        }

                        sendToStatist('editor.link.apply', {
                            highlight: !view.state.selection.empty,
                            insert: true,
                            text: true,
                            protocol: hasProtocol(textContent.trim()),
                        });

                        return this.editor.commands.addLinkToSelection({
                            href: link.href,
                        });
                    },
                    decorations: (state) => {
                        const pluginState = linkPluginKey.getState(state);
                        const {selection, doc} = state;

                        if (!pluginState.shouldHighlight || selection.empty) {
                            return DecorationSet.empty;
                        }

                        const {from, to} = selection;
                        const firstCharAttrs: Record<string, string> = {[dataLinkPopoverTarget]: ''};
                        const fullRangeAttrs: Record<string, any> = {
                            [dataLinkPreSelectionAttribute]: '',
                            style: 'color: rgb(var(--time-webkit-color-link-color-rgb))',
                        };

                        // inline-декоратор на весь диапазон
                        const fullRangeDeco = Decoration.inline(from, to, fullRangeAttrs);

                        // inline-декоратор только на первый символ
                        const firstCharDeco = Decoration.inline(from, from + 1, firstCharAttrs);

                        return DecorationSet.create(doc, [fullRangeDeco, firstCharDeco]);
                    },
                },
            }),
        ];
    },
}).configure({
    openOnClick: false,
    linkOnPaste: false, // выключаем плагин для вставки ссылки из оригинального экстеншена
    protocols: Array.from(protocols),
});

function markApplies(doc: Node, ranges: SelectionRange[], type: MarkType, enterAtoms: boolean) {
    for (const next of ranges) {
        const {$from, $to} = next;
        let can = $from.depth === 0 ? doc.inlineContent && doc.type.allowsMarkType(type) : false;
        doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            if (can || (!enterAtoms && node.isAtom && node.isInline && pos >= $from.pos && pos + node.nodeSize <= $to.pos)) {
                return false;
            }
            can = node.inlineContent && node.type.allowsMarkType(type);
        });
        if (can) {
            return true;
        }
    }
    return false;
}

function filterRanges(ranges: readonly SelectionRange[], excludedMarks: LinkOptions['excludedMarks']): SelectionRange[] {
    const result = [];
    for (const next of ranges) {
        const {$from: $initialFrom, $to} = next;
        let $from = $initialFrom;

        const hasMark = (mark: Mark, node: Node) => {
            return node.marks.map((mark) => mark.type.name).includes(mark.name);
        };

        const getNodeText = (node: Node, nodeStart: number) => {
            const start = Math.max(0, $from.pos - nodeStart);
            const end = Math.min(node.nodeSize, $to.pos - nodeStart);
            return node.textBetween(start, end);
        };

        $from.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            let skip = false;
            if (node.isText && node.text && (/^\s*$/).test(getNodeText(node, pos))) {
                skip = true;
            }
            if (excludedMarks.some((mark) => hasMark(mark, node))) {
                skip = true;
            }

            if (skip) {
                result.push(new SelectionRange($from, $from.doc.resolve(pos)));
                $from = $from.doc.resolve(Math.min(pos + node.nodeSize, $to.pos));
                return false;
            }
        });
        if ($from.pos < $to.pos) {
            result.push(new SelectionRange($from, $to));
        }
    }
    return result;
}
